<template>
    <div ref="trackListContainer">
    <TransitionGroup name="track-list" tag="div" class="track-list">
        <div v-for="(file, index) in displayedTracks"
            :key="file.id"
            class="track-row"
            :class="{ 'drag-over': dragOverIndex === index }"
            @click="selectedIndex = index"
            @dragover.prevent="onReorderDragOver(file.id, index, $event)"
            @dragenter.prevent="onReorderDragEnter(file.id, index, $event)"
            @dragleave.prevent="onReorderDragLeave(index, $event)"
            @drop.prevent="onReorderDrop(file.id, index, $event)"
        >
            <div class="file-item-wrapper">
                <div class="file-item" :draggable="!isShared"
                    @dragstart="onDragStart(file.id, $event)" @dragend="onDragEnd">
                    <TrackItem :file="file" :currentProjectId="activeProject?.id" :playlist="getPlaylist()"
                        :playlist-source="playlistSource" data-track-id="file.id" :isShared="isShared"
                        :isSelected="selectedIndex === index" />
                </div>
            </div>
        </div>
    </TransitionGroup>
    <Transition name="fade" mode="out-in">
        <div v-if="!isShared && this.showDelete">
            <div v-if="this.showDelete" class="remove" @dragover.prevent @dragenter.prevent="onRemoveDragEnter"
                @dragleave.prevent="onRemoveDragLeave" @drop.prevent="onRemoveDrop"
                :class="{ 'remove-drag-over': isRemoveDragOver }">
                <i class="bi bi-trash"></i><br>
                <b>{{ $t('dragDrop.deleteDrop') }}</b>
            </div>
        </div>

    </Transition>
    <div v-if="serverPaginated && loadError" class="load-more-sentinel">
        <button class="btn-new" type="button" @click="$emit('load-more')">Retry</button>
    </div>
    <div v-else-if="canLoadMore" ref="loadMoreSentinel" class="load-more-sentinel">
        <span class="loading-indicator">{{ loadingMore ? $t('common.loading') : '' }}</span>
    </div>
    </div>
</template>

<script>
import TrackItem from './TrackItem.vue';
import storageService from '@/scripts/services/StorageService';
import { playerActions, playerStore } from '@/scripts/services/PlayerService';

export default {
    name: "ExplorerComponent",
    components: {TrackItem},
    emits: ['load-more'],
    props: {
        tracks: {
            type: Array,
            default: () => []
        },
        activeProject: {
            type: Object,
            default: null
        },
        isShared: {
            type: Boolean,
            default: false
        },
        allowReorder: {
            type: Boolean,
            default: false
        },
        displayLimit: {
            type: Number,
            default: 15
        },
        serverPaginated: {
            type: Boolean,
            default: false
        },
        hasMore: {
            type: Boolean,
            default: false
        },
        loadingMore: {
            type: Boolean,
            default: false
        },
        loadError: {
            type: String,
            default: null
        }
    },
    data(){
        return {
            showDelete: false,
            isRemoveDragOver: false,
            selectedIndex: -1, // Currently selected track index for keyboard navigation
            displayedCount: this.displayLimit, // Initial number of tracks to display
            batchSize: 10, // Number of tracks to load on each scroll
            intersectionObserver: null,
            dragOverIndex: null,
        }
    },
    computed: {
        displayedTracks() {
            if (!this.tracks) return [];
            if (this.serverPaginated) return this.tracks;
            return this.tracks.slice(0, this.displayedCount);
        },
        hasMoreTracks() {
            return this.tracks && this.displayedCount < this.tracks.length;
        },
        canLoadMore() {
            return this.serverPaginated ? this.hasMore : this.hasMoreTracks;
        },
        playlistSource() {
            if (this.activeProject) return 'project';
            if (this.isShared) return 'shared';
            return this.serverPaginated ? 'standalone' : 'adHoc';
        }
    },
    methods: {
        getPlaylist() {
            if (this.activeProject != null) {
                return this.activeProject.tracks || []
            } else {
                return this.tracks
            }
        },
        onDragStart(trackId, event) {
            if (this.isShared) {
                return;
            }
            if (trackId) {
                this.showDelete = true;
                event.dataTransfer.setData('text/plain', `track:${trackId}`);
                event.dataTransfer.effectAllowed = 'move';
                event.target.classList.add('dragging');
            }
        },
        getDraggedTrackId(event) {
            const payload = event?.dataTransfer?.getData('text/plain') || '';
            if (!payload || payload.startsWith('project:')) {
                return null;
            }
            return payload.startsWith('track:') ? payload.slice(6) : payload;
        },
        onDragEnd() {
            if (this.isShared) {
                return;
            }
            this.showDelete = false;
            this.isRemoveDragOver = false;
            this.dragOverIndex = null;
            document.querySelectorAll('.dragging').forEach(el => {
                el.classList.remove('dragging');
            });
            document.querySelectorAll('.drag-over').forEach(el => {
                el.classList.remove('drag-over');
            });
        },
        onRemoveDragEnter(event) {
            if (this.isShared) {
                return;
            }
            event.preventDefault();
            this.isRemoveDragOver = true;
        },
        onRemoveDragLeave(event) {
            if (this.isShared) {
                return;
            }
            event.preventDefault();
            // Only remove the class if leaving the element completely
            if (!event.currentTarget.contains(event.relatedTarget)) {
                this.isRemoveDragOver = false;
            }
        },
        onRemoveDrop(event) {
            if (this.isShared) {
                return;
            }
            event.preventDefault();
            this.isRemoveDragOver = false;

            const trackId = this.getDraggedTrackId(event);
            if (trackId) {
                storageService.deleteTrack(trackId);
            }
        },
        onReorderDragEnter(trackId, index, event) {
            if (this.isShared || !this.activeProject || !this.allowReorder) {
                return;
            }
            const draggedId = this.getDraggedTrackId(event);
            if (!draggedId || String(draggedId) === String(trackId)) {
                return;
            }
            this.dragOverIndex = index;
        },
        onReorderDragOver(trackId, index, event) {
            if (this.isShared || !this.activeProject || !this.allowReorder) {
                return;
            }
            const draggedId = this.getDraggedTrackId(event);
            if (!draggedId || String(draggedId) === String(trackId)) {
                return;
            }
            event.dataTransfer.dropEffect = 'move';
            this.dragOverIndex = index;
        },
        onReorderDragLeave(index, event) {
            if (this.isShared || !this.activeProject || !this.allowReorder) {
                return;
            }
            if (!event.currentTarget.contains(event.relatedTarget)) {
                if (this.dragOverIndex === index) {
                    this.dragOverIndex = null;
                }
            }
        },
        async onReorderDrop(targetTrackId, index, event) {
            if (this.isShared || !this.activeProject || !this.allowReorder) {
                return;
            }
            const draggedId = this.getDraggedTrackId(event);
            this.dragOverIndex = null;
            if (!draggedId || String(draggedId) === String(targetTrackId)) {
                return;
            }

            const order = (this.tracks || []).map(track => track.id);
            const fromIndex = order.findIndex(id => String(id) === String(draggedId));
            const toIndex = order.findIndex(id => String(id) === String(targetTrackId));
            if (fromIndex === -1 || toIndex === -1) {
                return;
            }
            const draggedValue = order[fromIndex];
            order.splice(fromIndex, 1);
            order.splice(toIndex, 0, draggedValue);

            try {
                await storageService.reorderProjectTracks(this.activeProject.id, order);
            } catch (error) {
                console.error('Failed to reorder project tracks:', error);
            }
        },
        handleKeydown(event) {
            if (!this.tracks || this.tracks.length === 0) return;

            // Ignore if user is typing in an input field
            if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') return;

            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    this.selectNext();
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    this.selectPrev();
                    break;
                case 'Enter':
                    event.preventDefault();
                    this.playSelected();
                    break;
            }
        },
        selectNext() {
            if (this.selectedIndex < this.tracks.length - 1) {
                this.selectedIndex++;
                // Load more if approaching the end of displayed tracks
                if (this.selectedIndex >= this.displayedCount - 3 && this.hasMoreTracks) {
                    this.loadMoreTracks();
                }
            }
            this.scrollToSelected();
        },
        selectPrev() {
            if (this.selectedIndex > 0) {
                this.selectedIndex--;
            }
            this.scrollToSelected();
        },
        playSelected() {
            if (this.selectedIndex >= 0 && this.selectedIndex < this.tracks.length) {
                const track = this.tracks[this.selectedIndex];
                // If this track is already playing, toggle pause
                if (playerStore.currentTrack?.id === track.id) {
                    playerStore.currentProjectId = this.activeProject?.id ?? null;
                    playerActions.togglePlay();
                } else {
                    playerActions.setPlaylist(this.getPlaylist(), this.activeProject?.id ?? null, this.playlistSource);
                    playerActions.playTrack(track.id);
                }
            }
        },
        scrollToSelected() {
            this.$nextTick(() => {
                const container = this.$refs.trackListContainer;
                if (container) {
                    const selectedEl = container.querySelector('.track.selected');
                    if (selectedEl) {
                        selectedEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }
            });
        },
        isSelected(index) {
            return this.selectedIndex === index;
        },
        handleGlobalClick(event) {
            const container = this.$refs.trackListContainer;
            if (container && !container.contains(event.target)) {
                this.selectedIndex = -1;
            }
        },
        loadMoreTracks() {
            if (this.serverPaginated) {
                if (this.hasMore && !this.loadingMore && !this.loadError) {
                    this.$emit('load-more');
                }
                return;
            }
            if (this.hasMoreTracks) {
                this.displayedCount += this.batchSize;
            }
        },
        setupIntersectionObserver() {
            if (this.intersectionObserver) {
                this.intersectionObserver.disconnect();
            }

            this.intersectionObserver = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting) {
                    this.loadMoreTracks();
                }
            }, {
                root: null,
                rootMargin: '100px',
                threshold: 0
            });

            this.$nextTick(() => {
                if (this.$refs.loadMoreSentinel) {
                    this.intersectionObserver.observe(this.$refs.loadMoreSentinel);
                }
            });
        }
    },
    watch: {
        canLoadMore(newVal) {
            if (newVal) {
                this.$nextTick(() => this.setupIntersectionObserver());
            }
        },
        tracks() {
            // Reset displayed count when tracks change (e.g., switching projects)
            if (!this.serverPaginated) this.displayedCount = this.displayLimit;
        },
        loadingMore(newVal, oldVal) {
            if (this.serverPaginated && oldVal && !newVal && this.hasMore && !this.loadError) {
                this.$nextTick(() => this.setupIntersectionObserver());
            }
        }
    },
    mounted() {
        window.addEventListener('keydown', this.handleKeydown);
        window.addEventListener('click', this.handleGlobalClick);
        if (this.canLoadMore) {
            this.setupIntersectionObserver();
        }
    },
    beforeUnmount() {
        window.removeEventListener('keydown', this.handleKeydown);
        window.removeEventListener('click', this.handleGlobalClick);
        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
        }
    }
}

</script>

<style>

.file-item-wrapper {
    display: flex;
    align-items: center;
    overflow: hidden;
}

.file-item {
    flex: 1;
    min-width: 0;
    overflow: hidden;
}

.track-list-enter-active,
.track-list-leave-active {
    transition: opacity 0.4s ease, transform 0.4s ease;
}

.track-list-enter-from,
.track-list-leave-to {
    opacity: 0;
    transform: translateY(6px);
}

.track-list-move {
    transition: transform 0.4s ease;
}

.track-row.drag-over {
    background: #2F2F2F;
    box-shadow: rgba(0, 0, 0, 0.15) 0px 5px 9px 0px;
}

.remove {
    text-align: center;
    color: #bbb;
    font-size: 1.5rem;
    margin-top: 2rem;
    margin-bottom: 2rem;
    padding: 2rem;
    border: 1px dashed #bbb;
    border-radius: 16px;
    cursor: pointer;
    transition: background-color 0.3s ease;
}

.remove:hover {
    background-color: #2F2F2F;
}

.remove-drag-over {
    background-color: #2F2F2F;
}

.load-more-sentinel {
    display: flex;
    justify-content: center;
    padding: 1rem;
    color: #666;
}

.loading-indicator {
    font-size: 0.9rem;
}
</style>
